/**
 * VAT arithmetic on decimal strings, in integer kopecks.
 *
 * The existing implementation pushes `Decimal(15,2)` and `Decimal(5,2)`
 * through `Number()` before dividing: for 104.50 at 22% the intermediate
 * 2299/122 lands on 18.844262295081966 and the reported VAT drifts from the
 * exact figure. Money is not a float, so nothing here converts to one — the
 * whole calculation runs on BigInt kopecks and the result is formatted back.
 */
const KOPECKS = 100n;
/** Percent is stored as Decimal(5,2), so two decimal places of precision. */
const PERCENT_SCALE = 100n;
function parseKopecks(amount) {
    const trimmed = amount.trim().replace(",", ".");
    if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
        throw new Error(`Not a decimal amount: ${amount}`);
    }
    const negative = trimmed.startsWith("-");
    const [whole, fraction = ""] = trimmed.replace("-", "").split(".");
    // Truncate beyond kopecks rather than rounding: the caller's input is
    // already a persisted Decimal(15,2), so extra digits mean a bug upstream.
    const padded = (fraction + "00").slice(0, 2);
    const value = BigInt(whole) * KOPECKS + BigInt(padded);
    return negative ? -value : value;
}
function formatKopecks(value) {
    const negative = value < 0n;
    const abs = negative ? -value : value;
    const whole = abs / KOPECKS;
    const fraction = abs % KOPECKS;
    return `${negative ? "-" : ""}${whole}.${fraction.toString().padStart(2, "0")}`;
}
/** Half-up division, the rounding Russian accounting expects. */
function divideRoundHalfUp(numerator, denominator) {
    if (denominator === 0n)
        throw new Error("Division by zero");
    const negative = numerator < 0n !== denominator < 0n;
    const a = numerator < 0n ? -numerator : numerator;
    const b = denominator < 0n ? -denominator : denominator;
    const quotient = a / b;
    const remainder = a % b;
    const rounded = remainder * 2n >= b ? quotient + 1n : quotient;
    return negative ? -rounded : rounded;
}
export function splitVat({ amount, percent, included, }) {
    const amountKop = parseKopecks(amount);
    const percentScaled = parseKopecks(String(percent)); // percent × 100
    if (percentScaled === 0n) {
        const formatted = formatKopecks(amountKop);
        return { net: formatted, vat: "0.00", gross: formatted };
    }
    if (included) {
        // vat = gross × p / (100 + p), with p scaled by 100 on both sides.
        const vatKop = divideRoundHalfUp(amountKop * percentScaled, PERCENT_SCALE * 100n + percentScaled);
        return {
            net: formatKopecks(amountKop - vatKop),
            vat: formatKopecks(vatKop),
            gross: formatKopecks(amountKop),
        };
    }
    // vat = net × p / 100
    const vatKop = divideRoundHalfUp(amountKop * percentScaled, PERCENT_SCALE * 100n);
    return {
        net: formatKopecks(amountKop),
        vat: formatKopecks(vatKop),
        gross: formatKopecks(amountKop + vatKop),
    };
}
